// src/components/instructor/StaffTimesheetBoard.js
//
// Pass 83C — the canonical Operations timesheet completion board (lives in Team, NOT the hidden Finance
// projection). For a selected week it shows required production staff, their submission status, attested
// hours, access state, and completion metrics; owner/admin/ops can toggle the season requirement, reopen a
// submitted week (without editing it), and export SUBMITTED hours (never pay/PII). Scope-bound to orgId.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { useOrgRuntime } from '../../hooks/useOrgRuntime';
import { normalizeTimesheetRecord, reopenTimesheet } from '../../utils/staffTimesheetPersistence';
import { authorizeOrgWrite } from '../../utils/operationalWriteGate';
import { weekIdentity, memberTimesheetRequired, timesheetExportRows, toCsv, TIMESHEET_EXPORT_COLUMNS, TIMESHEET_STATUS } from '../../utils/staffTimesheet';
import { zonedShiftDays } from '../../utils/opsTime';

const C = { card: '#FFFFFF', card2: '#F5F4F0', border: '#E2E0DB', blue: '#2563EB', green: '#16A34A', amber: '#D97706', red: '#DC2626', redDim: '#FEE2E2', t1: '#1A1917', t2: '#6B6860', t3: '#9E9B95' };
const font = "'DM Sans',sans-serif";
const hrs = (m) => `${(Number(m || 0) / 60).toFixed(1)}h`;
const dayLabel = (key) => { if (!key) return ''; const [y, m, d] = key.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short' }); };

export default function StaffTimesheetBoard({ members = [], organizationId = '' }) {
  const { userData, currentUser } = useAuth();
  const { tz: orgTz, seasonId } = useOrgRuntime(organizationId, null);
  // owner/admin/ops may reopen + toggle the requirement (the toggle itself goes through the narrow callable).
  const canManage = authorizeOrgWrite({ userData, organizationId, action: 'assignment' }).ok;
  const [weekOffset, setWeekOffset] = useState(-1);
  const [filter, setFilter] = useState('all');
  // Pass 83C.2 §10/§11 — the parent shell caches `members` (one-shot load), so after a successful
  // setTimesheetRequired callable the prop is stale. Keep a CONFIRMED override map (memberUserId → bool) that
  // is set only AFTER the server call succeeds, so the checkbox / required-denominator / metrics update live.
  const [reqOverride, setReqOverride] = useState({});
  const memberRequired = useCallback((m) => (reqOverride[m.userId] != null ? reqOverride[m.userId] : memberTimesheetRequired(m, seasonId)), [reqOverride, seasonId]);
  const week = useMemo(() => weekIdentity(zonedShiftDays(new Date(), weekOffset * 7, orgTz || 'UTC'), orgTz || 'UTC'), [weekOffset, orgTz]);

  // Scope-bound query of this org's timesheets for the selected week. §12 — a query FAILURE must NOT read as
  // "all not started": it enters an error state and suppresses the (misleading) completion metrics.
  const [snap, setSnap] = useState({ scopeKey: '', byMember: {}, loaded: false, error: false });
  const scopeKey = `${organizationId}::${week.weekStartKey}`;
  useEffect(() => {
    setSnap({ scopeKey, byMember: {}, loaded: false, error: false });
    if (!organizationId) return undefined;
    const q = query(collection(db, 'staffTimesheets'), where('organizationId', '==', organizationId), where('weekStartKey', '==', week.weekStartKey));
    const unsub = onSnapshot(q, (s) => {
      const byMember = {};
      s.docs.forEach((d) => { const t = normalizeTimesheetRecord(d, d.id); byMember[t.memberUserId] = t; });
      setSnap({ scopeKey, byMember, loaded: true, error: false });
    }, (e) => { console.error('Timesheet board listener error:', e); setSnap({ scopeKey, byMember: {}, loaded: true, error: true }); });
    return () => unsub();
  }, [organizationId, week.weekStartKey, scopeKey]);
  const matched = snap.scopeKey === scopeKey; // synchronous scope binding
  const byMember = useMemo(() => (matched && !snap.error ? snap.byMember : {}), [matched, snap]);
  const boardError = matched && snap.error;
  const boardLoading = !matched || !snap.loaded;

  // Required production staff (TEST + not-required excluded from the denominator §52).
  const requiredMembers = useMemo(
    () => (members || []).filter((m) => memberRequired(m)),
    [members, memberRequired],
  );

  const rows = useMemo(() => requiredMembers.map((m) => {
    const t = byMember[m.userId] || null;
    const linked = !!m.authUserId;
    const status = !linked ? 'access_not_activated' : t ? t.status : 'not_started';
    return {
      member: m, name: m.name || m.displayName || m.userId, linked,
      status, teachingMinutes: t?.teachingMinutes || 0, otherMinutes: t?.otherMinutes || 0, totalMinutes: t?.totalAttestedMinutes || 0,
      submittedAtIso: t?.submittedAtIso || '', timesheet: t,
    };
  }), [requiredMembers, byMember]);

  const metrics = useMemo(() => ({
    required: rows.length,
    submitted: rows.filter((r) => r.status === TIMESHEET_STATUS.SUBMITTED).length,
    outstanding: rows.filter((r) => r.linked && r.status !== TIMESHEET_STATUS.SUBMITTED).length,
    accessNotActivated: rows.filter((r) => !r.linked).length,
  }), [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (filter === 'submitted') return r.status === TIMESHEET_STATUS.SUBMITTED;
    if (filter === 'outstanding') return r.linked && r.status !== TIMESHEET_STATUS.SUBMITTED;
    if (filter === 'attention') return r.status === TIMESHEET_STATUS.REOPENED || !r.linked;
    return true;
  }), [rows, filter]);

  const doReopen = useCallback(async (r) => {
    if (!r.timesheet || !canManage) return;
    const reason = window.prompt('Reopen reason (optional):') || '';
    // §16/§17 — the audit actor MUST be the real Firebase Auth uid (rules enforce == request.auth.uid).
    try { await reopenTimesheet(db, { id: r.timesheet.id, reason, reopenedByAuthUserId: currentUser?.uid || '' }); } catch (e) { window.alert(e?.message || 'Could not reopen'); }
  }, [canManage, currentUser]);

  const doToggle = useCallback(async (m, required) => {
    if (!canManage) return;
    try {
      // §30 — the narrow server callable (owner/admin/ops) writes ONLY timesheetRequired; no client member write.
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'setTimesheetRequired');
      await fn({ orgId: organizationId, memberUserId: m.userId, seasonId, required });
      setReqOverride((o) => ({ ...o, [m.userId]: required })); // §11 — reflect ONLY after server success
    } catch (e) { window.alert(e?.message || 'Could not update'); } // failure → old value stays (no override)
  }, [canManage, organizationId, seasonId]);

  const exportCsv = useCallback(() => {
    const submitted = Object.values(byMember).filter((t) => t.status === TIMESHEET_STATUS.SUBMITTED);
    const nameById = Object.fromEntries((members || []).map((m) => [m.userId, m.name || m.displayName || m.userId]));
    const csv = toCsv(TIMESHEET_EXPORT_COLUMNS, timesheetExportRows(submitted, nameById));
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `timesheet-hours-${organizationId}-${week.weekStartKey}.csv`; a.click(); URL.revokeObjectURL(url);
  }, [byMember, members, organizationId, week.weekStartKey]);

  return (
    <div data-testid="timesheet-board" style={{ fontFamily: font, color: C.t1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Timesheets</h3>
        <span style={{ fontSize: 13, color: C.t2 }}>Week of {dayLabel(week.weekStartKey)} – {dayLabel(week.weekEndKey)}</span>
        <button type="button" onClick={() => setWeekOffset((w) => w - 1)} style={btn}>‹ Prev</button>
        <button type="button" onClick={() => setWeekOffset(-1)} style={btn}>Last week</button>
        <button type="button" onClick={() => setWeekOffset((w) => Math.min(0, w + 1))} style={btn}>Next ›</button>
        <span style={{ flex: 1 }} />
        <button type="button" data-testid="ts-export" onClick={exportCsv} style={{ ...btn, borderColor: C.blue, color: C.blue }}>Export hours (CSV)</button>
      </div>

      {boardError ? (
        <div data-testid="ts-board-error" style={{ background: C.redDim || '#FEE2E2', border: `1px solid ${C.red}`, borderRadius: 12, padding: '14px 16px', color: C.red, fontSize: 13.5, marginBottom: 14 }}>
          Unable to verify timesheet completion — the timesheet records could not be loaded. Completion metrics are hidden
          until this is resolved. Reopen the tab or retry.
        </div>
      ) : boardLoading ? (
        <div style={{ color: C.t3, fontSize: 13, marginBottom: 14 }}>Loading timesheet completion…</div>
      ) : (
        <div data-testid="ts-metrics" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <Metric label="Required" value={metrics.required} />
          <Metric label="Submitted" value={metrics.submitted} color={C.green} />
          <Metric label="Outstanding" value={metrics.outstanding} color={C.amber} />
          <Metric label="Access not activated" value={metrics.accessNotActivated} color={C.t2} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {['all', 'outstanding', 'submitted', 'attention'].map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} style={{ ...btn, background: filter === f ? C.blue : C.card, color: filter === f ? '#fff' : C.t2, textTransform: 'capitalize' }}>{f}</button>
        ))}
      </div>

      {requiredMembers.length === 0 ? (
        <div style={{ fontSize: 13, color: C.t3, background: C.card, border: `1px dashed ${C.border}`, borderRadius: 12, padding: 18 }}>
          No staff are set to require a timesheet for this season yet. Enable it per person below (owner/admin).
        </div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
          {filtered.map((r) => (
            <div key={r.member.userId} data-testid={`ts-row-${r.member.userId}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: C.card, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 160, fontWeight: 700 }}>{r.name}</div>
              <StatusChip status={r.status} />
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 12, color: C.t2 }}>Teach {hrs(r.teachingMinutes)} · Other {hrs(r.otherMinutes)} · <strong>{hrs(r.totalMinutes)}</strong></span>
              {r.status === TIMESHEET_STATUS.SUBMITTED && canManage && <button type="button" data-testid={`ts-reopen-${r.member.userId}`} onClick={() => doReopen(r)} style={{ ...btn, borderColor: C.amber, color: C.amber }}>Reopen</button>}
            </div>
          ))}
        </div>
      )}

      {/* Requirement control (owner/admin per member-write rules) */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.t2, marginBottom: 6 }}>Who submits a timesheet ({seasonId})</div>
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
          {(members || []).filter((m) => !m.testAccount).map((m) => (
            <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: `1px solid ${C.border}`, background: C.card }}>
              <div style={{ minWidth: 160, fontSize: 13 }}>{m.name || m.displayName || m.userId}</div>
              <span style={{ flex: 1 }} />
              <label style={{ fontSize: 12, color: C.t2, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" data-testid={`ts-required-${m.userId}`} disabled={!canManage} checked={memberRequired(m)} onChange={(e) => doToggle(m, e.target.checked)} />
                Timesheet required
              </label>
            </div>
          ))}
        </div>
        {!canManage && <div style={{ fontSize: 11, color: C.t3, marginTop: 6 }}>Only an owner, admin, or operations user can change who submits a timesheet.</div>}
      </div>
    </div>
  );
}

const btn = { padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.t2, cursor: 'pointer', fontSize: 12.5, fontWeight: 700 };
function Metric({ label, value, color }) { return <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 14px' }}><div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: C.t3 }}>{label}</div><div style={{ fontSize: 20, fontWeight: 800, color: color || C.t1 }}>{value}</div></div>; }
function StatusChip({ status }) {
  const map = { submitted: { t: 'Submitted', c: C.green }, draft: { t: 'Draft', c: C.blue }, reopened: { t: 'Needs attention', c: C.amber }, not_started: { t: 'Not started', c: C.t3 }, access_not_activated: { t: 'Access not activated', c: C.t2 } };
  const s = map[status] || map.not_started;
  return <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', background: s.c, borderRadius: 20, padding: '2px 10px' }}>{s.t}</span>;
}
